import React from "react";
import { SitesEmissionsDTO, SitesEmissionsQuery, EmissionsAllocationOverviewRequestBody, KPISummaryRequest } from "api";
import { Loading } from "components";
import { SCORECARD_PAGE } from "pages/authenticated/scorecard";
import { isFeatureEnabled } from "utils";
import { ConsumptionSummary, EmissionTableComponent, locationBasedColumns, marketColumns, intensityColumns, EmissionAllocationSummaryTab, CustomKpisSummaryTab } from "../components";

const getBaseTabs = (
    currentEmissionsData: SitesEmissionsDTO | null,
    siteAggregations: NonNullable<SitesEmissionsDTO["siteAggregations"]> | null,
    loadingSummaryData: boolean,
    loadingSiteData: boolean,
    isDisaggregated: boolean,
    handleChangeIsDisaggregated: (isDisaggregated: boolean) => void,
    exportParams: SitesEmissionsQuery | undefined
) => [
        {
            label: "Summary",
            content: currentEmissionsData ? <ConsumptionSummary
                sitesEmissions={currentEmissionsData}
                isLoading={loadingSummaryData}
            /> : <Loading />,
        },
        {
            label: "Location Based",
            content: loadingSiteData ? <Loading /> : <EmissionTableComponent
                complementaryColumns={locationBasedColumns}
                isDisaggregated={isDisaggregated}
                setIsDisaggregated={handleChangeIsDisaggregated}
                tableData={siteAggregations ?? currentEmissionsData?.siteAggregations}
                exportParams={exportParams}
            />,
        },
        {
            label: "Market Based",
            content: loadingSiteData ? <Loading /> : <EmissionTableComponent
                complementaryColumns={marketColumns}
                isDisaggregated={isDisaggregated}
                setIsDisaggregated={handleChangeIsDisaggregated}
                tableData={siteAggregations ?? currentEmissionsData?.siteAggregations}
                exportParams={exportParams}
            />,
        },
        {
            label: "Emissions Intensity",
            content: loadingSiteData ? <Loading /> : <EmissionTableComponent
                complementaryColumns={intensityColumns}
                isDisaggregated={isDisaggregated}
                setIsDisaggregated={handleChangeIsDisaggregated}
                tableData={siteAggregations ?? currentEmissionsData?.siteAggregations}
                exportParams={exportParams}
            />,
        },
    ];

export const getAllTabs = (
    isAdminUser: boolean,
    currentEmissionsData: SitesEmissionsDTO | null,
    siteAggregations: NonNullable<SitesEmissionsDTO["siteAggregations"]> | null,
    loadingSummaryData: boolean,
    loadingSiteData: boolean,
    isDisaggregated: boolean,
    handleChangeIsDisaggregated: (isDisaggregated: boolean) => void,
    exportParams: SitesEmissionsQuery | undefined,
    configParameter: any,
    onKpiRefetchReady?: (refetchFn: (query: any) => void) => void
) => {
    const baseTabs = getBaseTabs(currentEmissionsData, siteAggregations, loadingSummaryData, loadingSiteData, isDisaggregated, handleChangeIsDisaggregated, exportParams);

    const tabs = [...baseTabs];

    if (isAdminUser && exportParams) {
        const allocationQuery: EmissionsAllocationOverviewRequestBody = {
            startDate: exportParams.startDate,
            endDate: exportParams.endDate,
            viewType: exportParams.viewType,
            isDisaggregated: exportParams.isDisaggregated,
            siteIds: exportParams.siteIds,
            businessUnits: exportParams.businessUnits,
            utilityTypeIds: exportParams.utilityTypeIds,
            countryRegions: exportParams.countryRegions,
        };
        tabs.push({
            label: 'Emissions Allocation',
            content: <EmissionAllocationSummaryTab query={allocationQuery} />
        });
    }

    // Only show Custom KPIs tab if premium feature is enabled
    if (isFeatureEnabled(configParameter, SCORECARD_PAGE.configParameter) && exportParams) {
        const kpiQuery: KPISummaryRequest = {
            startDate: exportParams.startDate,
            endDate: exportParams.endDate,
            siteIds: exportParams.siteIds ?? null,
            businessUnits: exportParams.businessUnits ?? null,
            countryRegions: exportParams.countryRegions ?? null,
            isDisaggregated: exportParams.isDisaggregated === 'true' || exportParams.isDisaggregated === true,
        };
        tabs.push({
            label: "Custom KPIs",
            content: <CustomKpisSummaryTab query={kpiQuery} onRefetchReady={onKpiRefetchReady || (() => { })} />
        });
    }

    return tabs;
}